/**
 * MemberAtmosphere — Federation Sports global visual atmosphere.
 *
 * Renders the fixed, decorative "field-lines + team halo + vignette"
 * layer defined in TEAM-COMMAND-CENTER-2B. Purely presentational:
 * `position: fixed`, `pointer-events: none`, `z-index: -1`. No child
 * component can be affected by it because it never enters the flow.
 *
 * Contract:
 *   - Mounted exactly once by the shared `Layout` in App.js when the
 *     current route path starts with `/member` (Member surfaces only).
 *   - Optional team colors: when a `team` prop with `.color` /
 *     `.secondary_color` is provided (2D+), the halo tints to team
 *     branding. Until 2D wires that up, we fall back to Federation
 *     Blue (institutional halo).
 *   - Respects `prefers-reduced-motion` implicitly — the atmosphere
 *     is fully static, no animation.
 */
import React from 'react';

const hexToHslTriplet = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const MemberAtmosphere = ({ teamPrimary, teamSecondary }) => {
  const style = {};
  const primaryHsl = hexToHslTriplet(teamPrimary);
  const secondaryHsl = hexToHslTriplet(teamSecondary);
  if (primaryHsl) style['--fs-team-primary'] = primaryHsl;
  if (secondaryHsl) style['--fs-team-secondary'] = secondaryHsl;

  return (
    <div
      className="fs-atmosphere"
      style={style}
      data-testid="fs-atmosphere"
      aria-hidden="true"
    >
      <div className="fs-atmosphere-halo" />
      <div className="fs-atmosphere-field" />
      <div className="fs-atmosphere-vignette" />
    </div>
  );
};

export default MemberAtmosphere;
